/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'L’Autre Moi',
  legalName: 'L’Autre Moi — Aline Tena',
  // No invented slogan: the baseline on their own sign ("À la r…e de soi") is
  // unreadable on the photos available, so this stays factual until she confirms it.
  tagline: 'Coiffure naturelle et bio à Grenoble',
  description:
    'Salon de coiffure au 41 rue Thiers à Grenoble : coloration 100 % végétale Terre de Couleur, soins aux plantes et à l’argile, coupes femme, homme et enfant. Du lundi au vendredi, de 9 h 30 à 19 h.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://www.example.fr',
  locale: 'fr_FR',

  contact: {
    // Left empty on purpose: the only address found (aline.tena@orange.fr, via the
    // Coiffeurs Justes directory) is unconfirmed — see the open questions.
    email: '',
    phone: '+33 4 76 85 41 84',
    address: '41 rue Thiers',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.1859367, lng: 5.7204243 },
  },

  // No account found for this salon — nothing to link.
  social: {},

  brand: {
    // Le vert du lettrage « L’Autre Moi » / l’argile de leurs soins.
    primary: '#37672F',
    secondary: '#8D4D3A',
    mode: 'light',
  },

  // Lundi–vendredi 9 h 30 – 19 h ; fermé samedi et dimanche (concordant sur la
  // fiche Google, Fresha et leur propre feuille de tarifs).
  openingHours: [
    { day: 1, open: '09:30', close: '19:00' },
    { day: 2, open: '09:30', close: '19:00' },
    { day: 3, open: '09:30', close: '19:00' },
    { day: 4, open: '09:30', close: '19:00' },
    { day: 5, open: '09:30', close: '19:00' },
  ],

  booking: {
    // Real per-service durations are unknown — the kit default stands until then.
    slotMinutes: 30,
    horizonDays: 30,
    services: [
      'Shampoing, brushing',
      'Shampoing, séchage',
      'Shampoing, coupe & coiffage — femme',
      'Shampoing, coupe & coiffage — homme',
      'Coupe junior',
      'Coupe enfant',
    ],
  },

  currency: 'EUR',
};

export default siteConfig;
